import React from "react";
import { FaClock, FaMapMarkerAlt, FaTicketAlt } from "react-icons/fa";
import "../../styles/theater-layout.css";

const formatShowTime = (showTime) => {
  if (!showTime) return "TBA";

  const showTimeText = String(showTime).trim();

  if (
    showTimeText.toLowerCase().includes("am") ||
    showTimeText.toLowerCase().includes("pm")
  ) {
    return showTimeText;
  }

  const parsedTime = new Date(`1970-01-01T${showTimeText}`);
  if (!Number.isNaN(parsedTime.getTime())) {
    return parsedTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  const fallbackDate = new Date(showTimeText);
  if (!Number.isNaN(fallbackDate.getTime())) {
    return fallbackDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  return showTimeText;
};

const formatShowDate = (showDate) => {
  if (!showDate) return "Today";

  const parsedDate = new Date(showDate);
  if (Number.isNaN(parsedDate.getTime())) return "Today";

  return parsedDate.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
  });
};

const shortShowId = (showId) => {
  if (!showId) return "------";
  return String(showId).slice(-6).toUpperCase();
};

const TheaterListLayout = ({ theaters, onMovieClick, onShowClick }) => {
  const renderAmenities = (amenities) => {
    if (!Array.isArray(amenities) || amenities.length === 0) return null;

    return (
      <div
        className="district-tlayout-amenities"
        aria-label="Theater amenities"
      >
        {amenities.slice(0, 4).map((amenity) => (
          <span key={amenity} className="district-tlayout-amenity-chip">
            {amenity}
          </span>
        ))}
        {amenities.length > 4 && (
          <span className="district-tlayout-amenity-chip is-muted">
            +{amenities.length - 4} more
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="district-tlayout-list">
      {theaters.map((theater, index) => {
        const theaterMovies = Array.isArray(theater?.movies)
          ? theater.movies
          : [];
        const theaterLocation = [
          theater?.address?.area,
          theater?.address?.city || theater?.city,
        ]
          .filter(Boolean)
          .join(", ");
        const screenCount = Array.isArray(theater?.screens)
          ? theater.screens.length
          : 0;
        const totalShows = theaterMovies.reduce(
          (sum, movie) =>
            sum + (Array.isArray(movie?.shows) ? movie.shows.length : 0),
          0,
        );

        return (
          <article key={theater._id || index} className="district-tlayout-card">
            <header className="district-tlayout-header">
              <div className="district-tlayout-header-info">
                <h3>{theater?.name || "Cinema"}</h3>
                <p>
                  <FaMapMarkerAlt aria-hidden="true" />
                  {theaterLocation || "Location not available"}
                </p>
              </div>

              <div className="district-tlayout-header-stats">
                <span>
                  {screenCount} Screen{screenCount === 1 ? "" : "s"}
                </span>
                <span>
                  {totalShows} Show{totalShows === 1 ? "" : "s"}
                </span>
              </div>
            </header>

            {renderAmenities(theater?.amenities)}

            <div className="district-tlayout-movies-wrap">
              {theaterMovies.length > 0 ? (
                theaterMovies.map((movie) => {
                  const movieShows = Array.isArray(movie?.shows)
                    ? movie.shows
                    : [];

                  return (
                    <section
                      key={movie?._id || movie?.title}
                      className="district-tlayout-movie-block"
                    >
                      <button
                        type="button"
                        className="district-tlayout-movie-title"
                        onClick={() =>
                          onMovieClick && onMovieClick(movie?._id, theater)
                        }
                      >
                        {movie?.title || "Untitled Movie"}
                      </button>

                      <div className="district-tlayout-show-grid">
                        {movieShows.length > 0 ? (
                          movieShows.map((show) => (
                            <button
                              type="button"
                              key={
                                show?._id || `${movie?._id}-${show?.showTime}`
                              }
                              className="district-tlayout-show-chip"
                              onClick={() =>
                                onShowClick && onShowClick(show?._id)
                              }
                              title={show?._id || "Show ID unavailable"}
                            >
                              <span className="district-tlayout-show-time">
                                <FaClock aria-hidden="true" />
                                {formatShowTime(show?.showTime)}
                              </span>
                              <span className="district-tlayout-show-meta">
                                <span>{formatShowDate(show?.showDate)}</span>
                                <span className="district-tlayout-show-id">
                                  <FaTicketAlt aria-hidden="true" /> ID{" "}
                                  {shortShowId(show?._id)}
                                </span>
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="district-tlayout-show-empty">
                            Showtimes coming soon
                          </div>
                        )}
                      </div>
                    </section>
                  );
                })
              ) : (
                <div className="district-tlayout-theater-empty">
                  No active movies available in this theater.
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default TheaterListLayout;
